import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowLeft, FiSave, FiCheck, FiLoader } = FiIcons;

const ModuleLayout = ({ title, description, icon: Icon, children, onSave, completed, canComplete, isLoading = false }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Sticky Header Actions */}
      <div className="bg-white/90 backdrop-blur-md shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors group" title="Return to Dashboard">
                <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm font-medium hidden sm:inline">Back to Dashboard</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-3 sm:space-x-4">
              {completed && (
                <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100">
                  <SafeIcon icon={FiCheck} className="text-sm" />
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-wider">Complete</span>
                </div>
              )}
              
              <button
                onClick={() => onSave(false)}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-700 rounded-lg transition-all disabled:opacity-50 shadow-sm"
              >
                <SafeIcon 
                  icon={isLoading ? FiLoader : FiSave} 
                  className={`text-sm ${isLoading ? 'animate-spin text-blue-500' : ''}`} 
                />
                <span className="text-sm font-bold">{isLoading ? 'Saving...' : 'Save'}</span>
              </button>

              {canComplete && (
                <button
                  onClick={() => onSave(true)}
                  disabled={isLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#2558ab] to-[#ff8057] hover:shadow-lg hover:brightness-110 text-white rounded-lg transition-all disabled:opacity-50 font-bold text-sm"
                >
                  <SafeIcon icon={FiCheck} className="text-sm" />
                  <span className="hidden sm:inline">Mark Complete</span>
                  <span className="sm:hidden">Finish</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Module Static Header */}
      <div className="bg-white">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2558ab] to-[#ff8057] flex items-center justify-center flex-shrink-0 shadow-xl shadow-blue-100">
              <SafeIcon icon={Icon} className="text-3xl text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-3 tracking-tight">{title}</h1>
              <p className="text-slate-600 leading-relaxed max-w-2xl text-lg font-medium">
                {description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Module Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
};

export default ModuleLayout;